import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface PowerStation {
  id?: number | string | null;
  name: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class RegisterApiService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getPowerStations(): Observable<any> {
    return this.http.get(`${this.baseUrl}power-station-dropdown`);
  }

  getAshCategories(stationId: string | number, msme?: string, products?: any[]): Observable<any> {
    let url = `${this.baseUrl}ash-category-dropdown/${stationId}`;
    const queryParams: string[] = [];
    if (msme) {
      queryParams.push(`msme=${encodeURIComponent(msme)}`);
    }
    if (products && products.length) {
      queryParams.push(`product=${encodeURIComponent(products.join(','))}`);
    }
    if (queryParams.length) {
      url += `?${queryParams.join('&')}`;
    }
    return this.http.get(url);
  }

  getNonManufactureCategoriesDropdown(): Observable<any> {
    return this.http.get(`${this.baseUrl}non-manufacture-categories-dropdown`);
  }

  getTariff(): Observable<any> {
    return this.http.get(`${this.baseUrl}tariff-dropdown`);
  }

  getFlyAshProducts(): Observable<any> {
    return this.http.get(`${this.baseUrl}fly-ash-products-dropdown`);
  }

  getStates(): Observable<any> {
    return this.http.get(`${this.baseUrl}state-dropdown`);
  }

  getDistricts(stateId: string | number): Observable<any> {
    return this.http.get(`${this.baseUrl}district-dropdown/${stateId}`);
  }

  getTaluks(districtId: string | number): Observable<any> {
    return this.http.get(`${this.baseUrl}taluk-dropdown/${districtId}`);
  }

  getVillages(talukId: string | number): Observable<any> {
    return this.http.get(`${this.baseUrl}village-dropdown/${talukId}`);
  }

  getDocumentTypes(): Observable<any> {
    return this.http.get(`${this.baseUrl}document-type-dropdown`);
  }

  getAshUnloadingFacilities(): Observable<any> {
    return this.http.get(`${this.baseUrl}ash_unloadind_facility-dropdown`);
  }

  uploadDocument(documentTypeId: number | string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('documentTypeId', documentTypeId.toString());
    formData.append('file', file);
    return this.http.post(`${this.baseUrl}document/upload`, formData);
  }

  downloadDocument(documentUploadId: string | number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}document/download/${documentUploadId}`, {
      responseType: 'blob',
    });
  }

  createRegistration(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}register/create`, payload);
  }

  createNonMnfgRegistration(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}register/NonManufactureCreate`, payload);
  }

  createTraderRegistration(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}register/TraderCreate`, payload);
  }

  saveRegistrationDraftStep(payload: {
    mobileNo: string;
    registrationType: number;
    step: number;
    formData: Record<string, unknown>;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}registration-draft/save-step`, payload);
  }

  getRegistrationDraftDetail(mobileNo: string): Observable<any> {
    return this.http.post(`${this.baseUrl}registration-draft/get-detail`, {
      mobileNo,
    });
  }

  // ─── Non-Manufacturing Draft APIs ──────────────────────────────────────────

  saveNonMnfgDraftStep(payload: {
    mobileNo: string;
    registrationType: number;
    step: number;
    formData: Record<string, unknown>;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}non-manufacture-draft/save-step`, payload);
  }

  getNonMnfgDraftDetail(mobileNo: string): Observable<any> {
    return this.http.post(`${this.baseUrl}non-manufacture-draft/get-detail`, { mobileNo });
  }

  // ─── Trader-specific Draft APIs ──────────────────────────────────────────

  saveTraderDraftStep(payload: {
    mobileNo: string;
    registrationType: number;
    step: number;
    formData: Record<string, unknown>;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}trader-draft/save-step`, payload);
  }

  getTraderDraftDetail(mobileNo: string): Observable<any> {
    return this.http.post(`${this.baseUrl}trader-draft/get-detail`, { mobileNo });
  }

  getRegistrationPreview(applicationNo: string, referenceNo: string): Observable<any> {
    return this.http.get(
      `${this.baseUrl}register/preview/${encodeURIComponent(applicationNo)}/${encodeURIComponent(referenceNo)}`,
    );
  }

  getRegView(q: string): Observable<any> {
    return this.http.get(`${this.baseUrl}reg-view?q=${encodeURIComponent(q)}`);
  }

  checkMobileExists(mobileNo: string): Observable<any> {
    return this.http.post(`${this.baseUrl}register/validate-mobile-number`, { mobileNo });
  }

  generateMobileOtp(mobileNo: string): Observable<any> {
    return this.http.post(`${this.baseUrl}otp/generate-mobile`, { mobileNo });
  }

  resendMobileOtp(mobileNo: string): Observable<any> {
    return this.http.post(`${this.baseUrl}otp/resend-mobile`, { mobileNo });
  }

  verifyMobileOtp(mobileNo: string, otp: string): Observable<any> {
    return this.http.post(`${this.baseUrl}otp/verify-mobile`, { mobileNo, otp });
  }

  generateEmailOtp(email: string): Observable<any> {
    return this.http.post(`${this.baseUrl}otp/generate-email`, { email });
  }

  resendEmailOtp(email: string): Observable<any> {
    return this.http.post(`${this.baseUrl}otp/resend-email`, { email });
  }

  verifyEmailOtp(email: string, otp: string): Observable<any> {
    return this.http.post(`${this.baseUrl}otp/verify-email`, { email, otp });
  }

  // Transport endpoints
  getTransportCapacityDropdown(): Observable<any> {
    return this.http.get(`${this.baseUrl}applicant/transport/capacity/dropdown`);
  }

  addTransport(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}applicant/transport`, payload);
  }

  getTransports(): Observable<any> {
    return this.http.get(`${this.baseUrl}applicant/transport`);
  }

  getAshCategoryDropdown(): Observable<any> {
    return this.http.get(`${this.baseUrl}applicant/advance-payments/ash-category/dropdown`);
  }

  submitApplicantAdvancePayment(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}applicant/advance-payments`, payload);
  }

  getApplicantAdvancePaymentList(page = 1, perPage = 10): Observable<any> {
    const params = new HttpParams().set('page', page.toString()).set('perPage', perPage.toString());
    return this.http.get(`${this.baseUrl}applicant/advance-payments`, { params });
  }

  getApplicantAdvancePaymentView(advancePaymentId: string | number): Observable<any> {
    return this.http.get(`${this.baseUrl}applicant/advance-payments/${advancePaymentId}`);
  }
  /**
   * Request OTP to check application status.
   * API: POST /application/status
   */
  requestApplicationStatus(applicationNo: string): Observable<any> {
    return this.http.post(`${this.baseUrl}application/status`, { applicationNo });
  }

  /**
   * Resend OTP for application status check.
   * API: POST /application/resendStatus
   */
  resendApplicationStatusOtp(applicationNo: string): Observable<any> {
    return this.http.post(`${this.baseUrl}application/resendStatus`, { applicationNo });
  }

  /**
   * Verify OTP and fetch application status details.
   * API: POST /application/verifyStatus
   */
  verifyApplicationStatus(applicationNo: string, otp: string): Observable<any> {
    return this.http.post(`${this.baseUrl}application/verifyStatus`, { applicationNo, otp });
  }

  /**
   * Get CTO flow options (Exempted / Non-Exempted etc.)
   * API: GET wsapi/cto-flow?product=<productIds>
   */
  getCtoFlow(msme?: string, products?: any[]): Observable<any> {
    let url = `${this.baseUrl}cto-flow`;
    const queryParams: string[] = [];
    if (msme) {
      queryParams.push(`msme=${encodeURIComponent(msme)}`);
    }
    if (products && products.length) {
      queryParams.push(`product=${encodeURIComponent(products.join(','))}`);
    }
    if (queryParams.length) {
      url += `?${queryParams.join('&')}`;
    }
    return this.http.get(url);
  }
}
